import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {CreateAVideoFeatureIntroTileProps} from './create-a-video-feature-intro-tile.types';
import styles from './create-a-video-feature-intro-tile.module.scss';
import {Button, Size, Type} from '@Components/button';
import {Text} from '@Components/text';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {ClickableDiv} from '@Components/clickable-div';
import {getAssetUrl} from '@Utils/s3.util';
import {AnimationCreationStatus} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {generateAnimationForAiChatImage} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {AiChatTurnToVideoSource, AiChatTurnToVideoType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {markAiChatCreateVideoIntroTileAsSeen} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.library';

export function CreateAVideoFeatureIntroTile({chatId, hashedId}: CreateAVideoFeatureIntroTileProps): ReactElement {
  const {state, actions} = useAiChat();
  const [hasPreviewVideoError, setHasPreviewVideoError] = useState<boolean>(false);

  const runAnimationGeneration = async (abortController: AbortController): Promise<void> => {
    try {
      const animation = await generateAnimationForAiChatImage(hashedId, {
        signal: abortController.signal,
        onPollingKeyReceived: (pollingKey: string): void => {
          actions.updateAnimationCreationState(chatId, hashedId, {pollingKey});
        },
      });
      actions.updateAnimationCreationState(chatId, hashedId, {
        status: AnimationCreationStatus.COMPLETED,
        animations: [animation],
        abortController: undefined,
        pollingKey: undefined,
      });
    } catch {
      if (!abortController.signal.aborted) {
        actions.updateAnimationCreationState(chatId, hashedId, {
          status: AnimationCreationStatus.FAILED,
          errorMessage: window.i18next.t('pmwjs_animation_creation_failed'),
          abortController: undefined,
          pollingKey: undefined,
        });
      }
    }
  };

  const handleTurnIntoVideo = (): void => {
    const abortController = new AbortController();

    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO, {
      [GA4EventParamName.SOURCE]: AiChatTurnToVideoSource.AD,
      [GA4EventParamName.TYPE]: AiChatTurnToVideoType.SILENT,
    });

    actions.updateActionTileStates({
      isCreateAVideoFeatureIntroVisible: false,
    });
    void markAiChatCreateVideoIntroTileAsSeen();
    actions.updateAnimationCreationState(chatId, hashedId, {
      isVisible: true,
      status: AnimationCreationStatus.CREATING,
      abortController,
    });

    void runAnimationGeneration(abortController);
  };

  const getHeader = (): ReactElement => {
    return (
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Text className={styles.title} val={window.i18next.t('pmwjs_create_a_video')} />
          <Text className={styles.subtitle} val={window.i18next.t('pmwjs_turn_design_into_a_video')} />
        </div>
        <Button
          type={Type.SECONDARY}
          size={Size.XSMALL}
          text={window.i18next.t('pmwjs_turn_into_video')}
          icon={'icon-video-ai'}
          onClick={handleTurnIntoVideo}
          customClasses={`radius-8 ${styles.turnIntoVideoButton}`}
        />
      </div>
    );
  };

  const getPreviewVideo = (): ReactElement => {
    const posterUrl = getAssetUrl('ai-chat-create-a-video-tile.webp');

    if (hasPreviewVideoError) {
      return <img className={styles.previewVideo} src={posterUrl} alt="" />;
    }

    return (
      <video
        className={styles.previewVideo}
        src={getAssetUrl('ai-chat-create-a-video-tile.webm')}
        poster={posterUrl}
        autoPlay
        loop
        muted
        playsInline
        onError={(): void => {
          setHasPreviewVideoError(true);
        }}
      />
    );
  };

  const getMobileTile = (): ReactElement => {
    return (
      <ClickableDiv className={styles.mobileTileCard} onClick={handleTurnIntoVideo}>
        <div className={styles.mobileTextContent}>
          <Text className={styles.title} val={window.i18next.t('pmwjs_create_a_video')} />
          <Text className={styles.subtitle} val={window.i18next.t('pmwjs_turn_design_into_a_video')} />
        </div>
        <video className={styles.mobileTileVideo} src={getAssetUrl('ai-chat-create-a-video-tile-mobile.webm')} autoPlay loop muted playsInline />
      </ClickableDiv>
    );
  };

  const getTile = (): ReactElement => {
    if (state.isSmall) {
      return getMobileTile();
    }

    return (
      <div className={styles.tileCard}>
        {getHeader()}
        {getPreviewVideo()}
      </div>
    );
  };

  return getTile();
}
